import { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Modal, Row } from "antd";
import { FaAddressCard } from "react-icons/fa6";
import dayjs from "dayjs";
import { CheckCircleOutlined, CloseOutlined } from "@ant-design/icons";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import { getDealerNo,getOemNo, hasRole } from "../../../global/hooks/getUserRole";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { booleanOptions, termOptions } from "../../../utils/Common";
import { LicenseCodeForm } from "../../LicenseCode/Components/LicenseCodeForm";

const formatDateTime = (value) => {
  if (!value) return null;
  return dayjs(value)
    .toDate()
    .toISOString()
    .replace(/(\.\d{3})Z$/, "$1000Z");
};

const isLifetimeTerm = (term) => {
  const months = Number(term);
  return !Number.isFinite(months) || months <= 0;
};

const calcEndTime = (start, term) => {
  if (!start) return null;
  const months = Number(term);
  if (!Number.isFinite(months) || months <= 0) return null;
  return dayjs(start).add(months, "month");
};

const resolveItemValue = (row, modifiedItems = []) => {
  if (row?.Item != null && row.Item !== "") {
    const n = Number(row.Item);
    if (Number.isFinite(n)) return n;
  }
  if (row?.ItemName) {
    const match = modifiedItems.find(
      (opt) =>
        String(opt.label).toLowerCase() === String(row.ItemName).toLowerCase(),
    );
    if (match != null) {
      const n = Number(match.value);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
};

export const PurchaseLicenseItemForm = ({
  purchaseId,
  deviceNo,
  editingItem,
  existingItems = [],
  onCancel,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const itemNo = editingItem?.ItemNo;
  const isEdit = itemNo != null;
  const selectedItem = Form.useWatch("Item", form);
  const selectedCodeNo = Form.useWatch("CodeNo", form);
  const selectedTerm = Form.useWatch("Term", form);
  const startTime = Form.useWatch("StartTime", form);
  const lifetime = isLifetimeTerm(selectedTerm);
  const editingCodeNo = editingItem?.CodeNo;
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const createdCodeRef = useRef(null);
  const canCreateCode = hasRole(1, 2);

  // --- dealer renewal permission ---
  const isSystemUser = hasRole(1, 2, 3);
  const isDealerUser = hasRole(7, 8, 9);

  const dealerNo = isDealerUser ? getDealerNo() : null;
  const oemNo = isDealerUser ? getOemNo() : null;  // needed since this endpoint is OEM-scoped

  const dealerListUrl =
    isDealerUser && oemNo
      ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${oemNo}/dealers/`
      : null;
  const { data: dealerListData } = useGet(dealerListUrl);
  const currentDealer = dealerListData?.data?.find(
        (d) => Number(d.value) === Number(dealerNo),
      );
  const dealerRenewalAllowed = Number(currentDealer?.renewal) === 1;

  const renewalManageable = isSystemUser || (isDealerUser && dealerRenewalAllowed);
  // --- end dealer renewal permission ---

  const postUrl =
    purchaseId &&
    `${process.env.REACT_APP_API_URL}/api/purchases/${purchaseId}/items/`;
  const putUrl =
    purchaseId && isEdit
      ? `${process.env.REACT_APP_API_URL}/api/purchases/${purchaseId}/items/${itemNo}/`
      : `${process.env.REACT_APP_API_URL}/api/purchases/${purchaseId || 0}/items/0/`;
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`;
  const codesUrl =
    selectedItem != null && selectedItem !== ""
      ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/?limit=0&sort_by=Item&order=asc&Item=${selectedItem}`
      : null;

  const { data: optionData, loading: optionDataLoading } = useGet(optionUrl);
  const {
    data: codesData,
    loading: codesLoading,
    refetch: codesRefetch,
  } = useGet(codesUrl);
  const modifiedItems = getModifiedResponse(optionData, "items");
  const licenseCodes = codesData?.data ?? [];

  const editingItemValue = isEdit
    ? resolveItemValue(editingItem, modifiedItems)
    : null;

  const usedItemValues = new Set();
  existingItems.forEach((row) => {
    if (isEdit && Number(row?.ItemNo) === Number(itemNo)) return;
    const value = resolveItemValue(row, modifiedItems);
    if (value != null) usedItemValues.add(value);
  });

  const itemOptions = modifiedItems.filter((opt) => {
    const value = Number(opt.value);
    if (isEdit && editingItemValue != null && editingItemValue === value) {
      return true;
    }
    return !usedItemValues.has(value);
  });

  const codeOptions = licenseCodes.map((item) => ({
    label: item.CodeName || String(item.Code),
    value: item.CodeNo,
  }));

  const isItemAlreadyUsed = (itemValue) => {
    const value = Number(itemValue);
    if (!Number.isFinite(value)) return false;
    if (isEdit && editingItemValue != null && editingItemValue === value) {
      return false;
    }
    return usedItemValues.has(value);
  };
  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);
  const {
    data: putData,
    loading: putLoading,
    error: putError,
    putData: putAction,
  } = usePut(putUrl);

  const saving = postLoading || putLoading;

  const fillTermAndPrice = (codeNo) => {
    const selected = licenseCodes.find(
      (item) => Number(item.CodeNo) === Number(codeNo),
    );
    if (!selected) return;
    const term = selected.Term;
    const start = form.getFieldValue("StartTime") || dayjs();
    form.setFieldsValue({
      Term: term,
      Price:
        selected.Price != null && selected.Price !== ""
          ? parseFloat(selected.Price).toFixed(2)
          : selected.Price,
      StartTime: start,
      EndTime: isLifetimeTerm(term) ? null : calcEndTime(start, term),
      Renewal: isLifetimeTerm(term) ? 0 : (form.getFieldValue("Renewal") ?? 0),
    });
  };

  useEffect(() => {
    if (isEdit) return;
    form.setFieldsValue({
      StartTime: dayjs(),
      Renewal: 0,
    });
  }, [isEdit, form]);

  useEffect(() => {
    if (!isEdit || !editingItem || optionDataLoading || !modifiedItems.length) {
      return;
    }
    const itemValue = resolveItemValue(editingItem, modifiedItems);
    if (itemValue == null) return;
    form.setFieldsValue({
      Item: itemValue,
      StartTime: editingItem.StartTime ? dayjs(editingItem.StartTime) : dayjs(),
      EndTime: editingItem.EndTime ? dayjs(editingItem.EndTime) : null,
      Renewal: editingItem.Renewal ?? 0,
    });
  }, [editingItem, isEdit, form, optionDataLoading, modifiedItems.length]);

  useEffect(() => {
    if (!isEdit || !editingItem || !licenseCodes.length) return;
    if (createdCodeRef.current != null) return;
    const currentItem = form.getFieldValue("Item");
    const originalItem = resolveItemValue(editingItem, modifiedItems);
    if (
      originalItem != null &&
      currentItem != null &&
      Number(currentItem) !== Number(originalItem)
    ) {
      return;
    }

    const match = licenseCodes.find(
      (item) =>
        Number(item.CodeNo) === Number(editingItem.CodeNo) ||
        (editingItem.Code != null && item.Code === editingItem.Code) ||
        (editingItem.CodeName &&
          String(item.CodeName).toLowerCase() ===
            String(editingItem.CodeName).toLowerCase()),
    );
    if (!match) return;

    const start = form.getFieldValue("StartTime") || dayjs();
    const term = match.Term ?? editingItem.Term;
    form.setFieldsValue({
      CodeNo: match.CodeNo,
      Term: term,
      Price:
        match.Price != null && match.Price !== ""
          ? parseFloat(match.Price).toFixed(2)
          : editingItem.Price != null && editingItem.Price !== ""
            ? Number(editingItem.Price).toFixed(2)
            : match.Price,
      EndTime: isLifetimeTerm(term)
        ? null
        : editingItem.EndTime
          ? dayjs(editingItem.EndTime)
          : calcEndTime(start, term),
      Renewal: editingItem.Renewal ?? 0,
    });
  }, [licenseCodes, isEdit, editingItem, form, modifiedItems.length]);

  useEffect(() => {
    if (selectedCodeNo == null || selectedCodeNo === "") return;
    if (!licenseCodes.length) return;
    if (
      isEdit &&
      editingCodeNo != null &&
      Number(selectedCodeNo) === Number(editingCodeNo)
    ) {
      return;
    }
    fillTermAndPrice(selectedCodeNo);
  }, [selectedCodeNo, codesData]);

  useEffect(() => {
    if (lifetime) {
      form.setFieldsValue({ EndTime: null, Renewal: 0 });
      return;
    }
    if (!startTime || selectedTerm == null || selectedTerm === "") return;
    form.setFieldsValue({
      EndTime: calcEndTime(startTime, selectedTerm),
    });
  }, [startTime, selectedTerm, lifetime, form]);
  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData && !isEdit) {
      openConfirmNotification("Success");
      onSaved?.();
      onCancel?.();
    }
  }, [postError, postData, postLoading, isEdit, onSaved, onCancel]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putLoading && !putError && putData && isEdit) {
      openConfirmNotification("Success");
      onSaved?.();
      onCancel?.();
    }
  }, [putError, putData, putLoading, isEdit, onSaved, onCancel]);

  const handleCodeCreated = (created) => {
    const codeNo = created?.CodeNo;
    if (codeNo == null) {
      codesRefetch?.();
      return;
    }
    createdCodeRef.current = codeNo;
    const createdItem = created?.Item != null ? Number(created.Item) : null;
    const itemChanged =
      createdItem != null && Number(selectedItem) !== createdItem;

    if (itemChanged && isItemAlreadyUsed(createdItem)) {
      codesRefetch?.();
      return;
    }
    form.setFieldsValue({
      ...(itemChanged ? { Item: createdItem } : {}),
      CodeNo: codeNo,
    });
    if (!itemChanged) codesRefetch?.();
  };

  const onFinish = async (values) => {
    if (values.CodeNo == null) {
      openErrorNotification("Code is required.");
      return;
    }
    if (deviceNo == null || deviceNo === "") {
      openErrorNotification("Site is not available.");
      return;
    }
    if (isItemAlreadyUsed(values.Item)) {
      openErrorNotification("This item is already added.");
      return;
    }

    const term = values.Term;
    const isLifetime = isLifetimeTerm(term);
    const start = values.StartTime || dayjs();
    const end = isLifetime ? null : calcEndTime(start, term);
    const renewal = isLifetime
      ? 0
      : renewalManageable
        ? (Number(values.Renewal) === 1 ? 1 : 0)
        : (isEdit ? (Number(editingItem?.Renewal) === 1 ? 1 : 0) : 0);

    const payload = {
      CodeNo: Number(values.CodeNo),
      StartTime: formatDateTime(start),
      EndTime: formatDateTime(end),
      Renewal: renewal,
    };

    try {
      if (isEdit) {
        await putAction(payload);
      } else {
        await postAction(payload);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
  };

  return (
    <>
      <Form
        validateTrigger="onChange"
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout="vertical"
        autoComplete="off"
      >
        <div className="px-2 pt-0 pb-3">
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <CustomSelect
                label="Item"
                name="Item"
                options={itemOptions}
                placeholder="Please select"
                required
                loading={optionDataLoading}
                onChange={(value) => {
                  if (isItemAlreadyUsed(value)) {
                    openErrorNotification("This item is already added.");
                    form.setFieldsValue({ Item: undefined });
                    return;
                  }
                  form.setFieldsValue({
                    CodeNo: undefined,
                    Term: undefined,
                    Price: undefined,
                    EndTime: null,
                    Renewal: 0,
                  });
                }}
              />
            </Col>
            <Col span={24}>
              <CustomSelect
                label="Code"
                name="CodeNo"
                options={codeOptions}
                placeholder="Please select"
                required
                disabled={selectedItem == null || selectedItem === ""}
                loading={codesLoading}
                onChange={fillTermAndPrice}
                handleCreateNew={
                  canCreateCode ? () => setCodeModalOpen(true) : null
                }
                type="Code"
              />
            </Col>
            <Col span={24}>
              <CustomSelect
                label="Term"
                name="Term"
                options={termOptions}
                placeholder="Please select"
                disabled
                allowClear={false}
                loading={codesLoading}
              />
            </Col>
            <Col span={24}>
              <CustomInput
                label="Price"
                name="Price"
                type="number"
                disabled
                prefix="$"
                style={{ height: "30px" }}
                loading={codesLoading}
              />
            </Col>
            <Col span={24}>
              <CustomDateTimePicker
                label="Start Time"
                name="StartTime"
                format="YYYY-MM-DD HH:mm"
                disablePast={!isEdit}
                required
              />
            </Col>
            {!lifetime ? (
              <Col span={24}>
                <CustomDateTimePicker
                  label="End Time"
                  name="EndTime"
                  format="YYYY-MM-DD HH:mm"
                  disabled
                />
              </Col>
            ) : null}
            {!lifetime ? (
              <Col span={24}>
                <CustomSelect
                  label="Renewal"
                  name="Renewal"
                  options={booleanOptions}
                  placeholder="Please select"
                  allowClear={false}
                  required={renewalManageable}
                  disabled={!renewalManageable}
                />
              </Col>
            ) : null}
          </Row>
        </div>
      </Form>
      <div className="flex justify-center space-x-2 mt-2.5">
        <Button
          type="primary"
          className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
          onClick={() => form.submit()}
          loading={saving}
          disabled={saving}
        >
          <CheckCircleOutlined className="!text-[14px] font-medium" />
          <span>Apply</span>
        </Button>
        <Button className="reset-button" onClick={onCancel}>
          <CloseOutlined />
          <span>Cancel</span>
        </Button>
      </div>
      <Modal
        open={codeModalOpen}
        closable={false}
        onCancel={() => setCodeModalOpen(false)}
        footer={null}
        width={580}
        centered
        destroyOnClose
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <FaAddressCard className="text-[15px] text-black ml-1" />
            <span className="text-[14px] font-[Poppins] text-black ml-2">
              Add Code
            </span>
          </div>
        }
      >
        <LicenseCodeForm
          isModalForm
          defaultValues={{ Item: selectedItem }}
          onCancel={() => setCodeModalOpen(false)}
          onCreated={handleCodeCreated}
        />
      </Modal>
    </>
  );
};